import { Component, ElementRef, OnDestroy, computed, inject, input, output, signal, viewChild } from '@angular/core'
import {
  LucideArrowLeft, LucideImageUp, LucideTrash2, LucideZoomIn, LucideZoomOut,
} from '@lucide/angular'
import { User } from '../../interfaces/user.interface'
import { AuthService } from '../../../auth/services/auth.service'
import { ProfilePhotoService } from '../../services/profile-photo.service'
import { AvatarComponent } from '../avatar/avatar.component'

/** Side of the square photo that is uploaded. The server stores it as sent (≤1024). */
const OUTPUT_SIZE = 512
/** Furthest zoom, as a multiple of "just covers the circle". */
const MAX_ZOOM = 4

/**
 * Change my photo, inside the account card (CC 05/10/2026: everyone can set their own image).
 * Choose a photo (on a phone the picker offers the camera too), drag and zoom it inside the
 * circle, Save. The crop is drawn to a 512px JPEG in the browser, so a phone uploads ~50 KB
 * rather than a 5 MB camera file, and the server re-encodes it again, dropping the GPS
 * position phones write into photos.
 */
@Component({
  selector: 'app-photo-editor',
  standalone: true,
  imports: [AvatarComponent, LucideArrowLeft, LucideImageUp, LucideTrash2, LucideZoomIn, LucideZoomOut],
  template: `
    <section class="pcard">
      <div class="phead">
        <button type="button" class="pback" aria-label="Back to your account" (click)="closed.emit()">
          <svg lucideArrowLeft [size]="20"></svg>
        </button>
        <h2 class="ptitle">{{ src() ? 'Position your photo' : 'Profile photo' }}</h2>
      </div>

      @if (src(); as url) {
        <!-- Drag to move, pinch / wheel / slider to zoom. The circle is what others see. -->
        <div #frame class="pframe"
             (pointerdown)="onPointerDown($event)"
             (pointermove)="onPointerMove($event)"
             (pointerup)="onPointerUp($event)"
             (pointercancel)="onPointerUp($event)"
             (wheel)="onWheel($event)">
          <img #img class="pimg" [src]="url" alt="" draggable="false"
               [style.width.px]="natural().w * scale()"
               [style.height.px]="natural().h * scale()"
               [style.transform]="'translate(' + offset().x + 'px,' + offset().y + 'px)'"
               (load)="onImageLoad()" (error)="onImageError()">
          <span class="pmask" aria-hidden="true"></span>
        </div>
        <div class="pzoom">
          <svg lucideZoomOut [size]="18" aria-hidden="true"></svg>
          <input type="range" min="1" [max]="maxZoom" step="0.01" aria-label="Zoom"
                 [value]="zoom()" (input)="setZoom(+$any($event.target).value)">
          <svg lucideZoomIn [size]="18" aria-hidden="true"></svg>
        </div>
        @if (error()) { <p class="perror" role="alert">{{ error() }}</p> }
        <div class="pactions">
          <button type="button" class="pbtn" (click)="choose()" [disabled]="busy()">Choose another</button>
          <button type="button" class="pbtn pbtn--primary" (click)="save()" [disabled]="busy() || !ready()">
            {{ busy() ? 'Saving…' : 'Save' }}
          </button>
        </div>
      } @else {
        <div class="pcurrent">
          <app-avatar [entity]="user()" [size]="112"></app-avatar>
        </div>
        <p class="phint">Clubs, coaches and teammates see this next to your name.</p>
        @if (error()) { <p class="perror" role="alert">{{ error() }}</p> }
        <div class="pactions pactions--stack">
          <button type="button" class="pbtn pbtn--primary" (click)="choose()" [disabled]="busy()">
            <svg lucideImageUp [size]="18"></svg>
            {{ hasPhoto() ? 'Change photo' : 'Add a photo' }}
          </button>
          @if (hasPhoto()) {
            <button type="button" class="pbtn" (click)="remove()" [disabled]="busy()">
              <svg lucideTrash2 [size]="18"></svg>
              {{ busy() ? 'Removing…' : 'Remove photo' }}
            </button>
          }
        </div>
      }

      <input #file type="file" accept="image/*" hidden (change)="onFile($event)">
    </section>
  `,
  styles: [`
    .pcard {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      padding: 10px 14px 16px;
      border-radius: 22px;
      background: var(--scios-surface-2);
    }
    .phead { align-self: stretch; display: flex; align-items: center; gap: 6px; }
    .pback {
      display: inline-flex; align-items: center; justify-content: center;
      width: 44px; height: 44px; margin-left: -8px;
      border: none; border-radius: 50%; background: transparent;
      color: var(--scios-text-secondary); cursor: pointer;
    }
    .ptitle { margin: 0; font-size: 16px; font-weight: 600; color: var(--scios-text-primary); }

    .pcurrent { padding: 6px 0 2px; }
    .phint { margin: 0; font-size: 13px; text-align: center; color: var(--scios-text-secondary); }

    .pframe {
      position: relative;
      width: min(260px, 100%);
      aspect-ratio: 1;
      overflow: hidden;
      border-radius: 16px;
      background: #000;
      touch-action: none;
      cursor: grab;
      user-select: none;
    }
    .pframe:active { cursor: grabbing; }
    .pimg {
      position: absolute;
      left: 50%;
      top: 50%;
      max-width: none;
      margin: 0;
      translate: -50% -50%;
      pointer-events: none;
    }
    /* Dim everything outside the circle, with a thin ring on its edge. */
    .pmask {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      box-shadow: 0 0 0 999px rgba(2, 5, 12, 0.62), inset 0 0 0 2px rgba(255, 255, 255, 0.85);
      pointer-events: none;
    }

    .pzoom {
      display: flex; align-items: center; gap: 10px;
      width: min(260px, 100%);
      min-height: 44px;
      color: var(--scios-text-secondary);
    }
    .pzoom input { flex: 1; accent-color: var(--accent-primary); }

    .perror { margin: 0; font-size: 13px; text-align: center; color: #EF4444; }

    .pactions { align-self: stretch; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .pactions--stack { grid-template-columns: 1fr; }
    .pbtn {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      min-height: 44px; padding: 0 14px;
      border-radius: 22px;
      border: 1px solid var(--scios-border);
      background: transparent;
      color: var(--scios-text-primary);
      font: inherit; font-size: 14px; font-weight: 600;
      cursor: pointer;
    }
    .pbtn--primary {
      border-color: var(--accent-primary);
      background: var(--accent-primary-15);
      color: var(--accent-primary);
    }
    .pbtn:disabled { opacity: 0.55; cursor: default; }
    @media (hover: hover) {
      .pbtn:not(:disabled):hover, .pback:hover {
        background: color-mix(in srgb, var(--scios-text-primary) 7%, transparent);
      }
    }
    .pbtn:focus-visible, .pback:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }
  `],
})
export class PhotoEditorComponent implements OnDestroy {
  private readonly photos = inject(ProfilePhotoService)
  private readonly auth = inject(AuthService)

  readonly user = input.required<User>()
  /** Back to the account card: after Save / Remove, or the back arrow. */
  readonly closed = output<void>()

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('file')
  private readonly frame = viewChild<ElementRef<HTMLElement>>('frame')
  private readonly img = viewChild<ElementRef<HTMLImageElement>>('img')

  protected readonly maxZoom = MAX_ZOOM
  protected readonly src = signal<string | null>(null)
  protected readonly natural = signal({ w: 0, h: 0 })
  protected readonly zoom = signal(1)
  protected readonly offset = signal({ x: 0, y: 0 })
  protected readonly ready = signal(false)
  protected readonly busy = signal(false)
  protected readonly error = signal('')

  protected readonly hasPhoto = computed(() => !!this.user().profile?.avatar?.trim())

  /** Width of the square frame on screen, read when needed (it follows the panel width). */
  private frameSize = 260
  /** Pixels on screen per pixel of the photo at zoom 1: the photo just covers the frame. */
  private readonly coverScale = signal(1)
  protected readonly scale = computed(() => this.coverScale() * this.zoom())

  private readonly pointers = new Map<number, { x: number, y: number }>()
  private pinchStart: { dist: number, zoom: number } | null = null

  ngOnDestroy (): void {
    this.revoke()
  }

  choose (): void {
    this.error.set('')
    this.fileInput()?.nativeElement.click()
  }

  onFile (event: Event): void {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = '' // choosing the same file again still fires change
    if (!file) return
    if (!file.type.startsWith('image/')) {
      this.error.set('That file is not a photo. Choose a JPEG or PNG.')
      return
    }
    this.revoke()
    this.ready.set(false)
    this.zoom.set(1)
    this.offset.set({ x: 0, y: 0 })
    this.src.set(URL.createObjectURL(file))
  }

  onImageLoad (): void {
    const el = this.img()?.nativeElement
    const frame = this.frame()?.nativeElement
    if (!el || !frame) return
    this.frameSize = frame.clientWidth || 260
    const w = el.naturalWidth
    const h = el.naturalHeight
    this.natural.set({ w, h })
    this.coverScale.set(this.frameSize / Math.min(w, h))
    this.ready.set(true)
  }

  onImageError (): void {
    this.revoke()
    this.src.set(null)
    this.error.set('This photo could not be opened. Try a JPEG or PNG.')
  }

  setZoom (value: number): void {
    this.zoom.set(Math.min(MAX_ZOOM, Math.max(1, value)))
    this.offset.set(this.clamp(this.offset()))
  }

  onWheel (event: WheelEvent): void {
    event.preventDefault()
    this.setZoom(this.zoom() * (event.deltaY < 0 ? 1.08 : 1 / 1.08))
  }

  onPointerDown (event: PointerEvent): void {
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (this.pointers.size === 2) {
      this.pinchStart = { dist: this.pointerDistance(), zoom: this.zoom() }
    }
  }

  onPointerMove (event: PointerEvent): void {
    const last = this.pointers.get(event.pointerId)
    if (!last) return
    const now = { x: event.clientX, y: event.clientY }
    this.pointers.set(event.pointerId, now)

    if (this.pointers.size >= 2 && this.pinchStart) {
      this.setZoom(this.pinchStart.zoom * this.pointerDistance() / Math.max(1, this.pinchStart.dist))
      return
    }
    const o = this.offset()
    this.offset.set(this.clamp({ x: o.x + now.x - last.x, y: o.y + now.y - last.y }))
  }

  onPointerUp (event: PointerEvent): void {
    this.pointers.delete(event.pointerId)
    if (this.pointers.size < 2) this.pinchStart = null
  }

  async save (): Promise<void> {
    const el = this.img()?.nativeElement
    if (!el || !this.ready()) return
    this.busy.set(true)
    this.error.set('')
    try {
      const blob = await this.renderCrop(el)
      const url = await this.photos.upload(blob)
      this.applyToCurrentUser(url)
      this.closed.emit()
    } catch {
      this.error.set('The photo could not be saved. Please try again.')
    } finally {
      this.busy.set(false)
    }
  }

  async remove (): Promise<void> {
    this.busy.set(true)
    this.error.set('')
    try {
      await this.photos.remove()
      this.applyToCurrentUser('')
      this.closed.emit()
    } catch {
      this.error.set('The photo could not be removed. Please try again.')
    } finally {
      this.busy.set(false)
    }
  }

  /**
   * Keeps the photo covering the whole frame: it can move only as far as its own overhang.
   * The photo is centred, so its overhang on each side is half the difference.
   */
  private clamp (o: { x: number, y: number }): { x: number, y: number } {
    const { w, h } = this.natural()
    const s = this.scale()
    const maxX = Math.max(0, (w * s - this.frameSize) / 2)
    const maxY = Math.max(0, (h * s - this.frameSize) / 2)
    return {
      x: Math.min(maxX, Math.max(-maxX, o.x)),
      y: Math.min(maxY, Math.max(-maxY, o.y)),
    }
  }

  /** The part of the photo inside the frame, as a square JPEG. */
  private renderCrop (el: HTMLImageElement): Promise<Blob> {
    const { w, h } = this.natural()
    const s = this.scale()
    const o = this.offset()
    // The frame's top-left corner, in photo pixels.
    const side = this.frameSize / s
    const sx = w / 2 - o.x / s - side / 2
    const sy = h / 2 - o.y / s - side / 2

    const canvas = document.createElement('canvas')
    canvas.width = OUTPUT_SIZE
    canvas.height = OUTPUT_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) return Promise.reject(new Error('no canvas'))
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE)
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(el, sx, sy, side, side, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE)

    return new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.9))
  }

  /** Every avatar on the page follows the signed-in user, so this updates them all at once. */
  private applyToCurrentUser (url: string): void {
    const user = this.auth.currentUser()
    if (!user) return
    const profile = { ...(user.profile ?? { id: user.id, phone_number: user.phone_number, bio: '', position: '', experience: 0 }), avatar: url }
    this.auth.updateCurrentUser({ ...user, profile })
  }

  private pointerDistance (): number {
    const [a, b] = [...this.pointers.values()]
    return Math.hypot(a.x - b.x, a.y - b.y)
  }

  private revoke (): void {
    const url = this.src()
    if (url) URL.revokeObjectURL(url)
  }
}
